import { useState, useRef } from 'react';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import DiscoveryCard from '@/components/feature/DiscoveryCard';
import { discoverySections } from '@/mocks/discovery';

export default function ExplorePage() {
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    const el = sectionRefs.current[sectionId];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Highlight briefly
      setTimeout(() => setActiveSection(null), 2000);
    }
  };

  const totalRadios = discoverySections.reduce((sum, s) => sum + s.data.length, 0);

  return (
    <div className="min-h-screen bg-background-50">
      <Header />

      <main>
        {/* ─── HERO ─── */}
        <section className="relative w-full overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20artistic%20radio%20frequency%20waves%20visualization%2C%20flowing%20warm%20amber%20and%20coral%20gradients%20on%20dark%20background%2C%20modern%20minimalist%20audio%20spectrum%20art%2C%20elegant%20curved%20waveforms%20with%20glowing%20nodes%2C%20sophisticated%20broadcast%20technology%20abstract%2C%20smooth%20flowing%20lines%20representing%20sound%20waves%2C%20digital%20art%20style%20with%20ethereal%20glow%20effects%2C%20warm%20tone%20color%20palette&width=1600&height=900&seq=discovery-hero-01&orientation=landscape"
            alt="Descubrí Radios RFW"
            className="w-full h-[320px] md:h-[440px] object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/40"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center w-full">
            <div className="w-16 h-16 rounded-2xl bg-background-50/15 backdrop-blur-md flex items-center justify-center mb-4 border border-background-50/20">
              <i className="ri-compass-3-line text-3xl text-background-50"></i>
            </div>
            <h1 className="text-3xl md:text-5xl font-extrabold text-background-50 leading-tight mb-3">
              Descubrí Radios
            </h1>
            <p className="text-sm md:text-base text-background-50/80 max-w-xl leading-relaxed">
              Explorá el universo de RadiofrecuenciaRFW. Nuevas estaciones, tendencias, las más activas y recomendaciones para vos.
            </p>
            <div className="flex items-center gap-4 mt-6">
              <span className="text-background-50/90 text-xs font-bold">
                {totalRadios}+ radios para descubrir
              </span>
              <div className="w-px h-4 bg-background-50/20"></div>
              <span className="text-background-50/70 text-xs">
                6 categorías de exploración
              </span>
            </div>
          </div>
        </section>

        {/* ─── QUICK NAV PILLS ─── */}
        <section className="bg-background-100 py-5 sticky top-0 z-40 border-b border-background-200/70">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1">
              <span className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider mr-1 whitespace-nowrap">
                Ir a:
              </span>
              {discoverySections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[11px] font-bold whitespace-nowrap transition-all cursor-pointer ${
                    activeSection === section.id
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 border border-background-200 hover:border-foreground-300 hover:text-foreground-900'
                  }`}
                >
                  <i className={`${section.icon} text-xs`}></i>
                  {section.title}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* ─── SECTIONS ─── */}
        {discoverySections.map((section, idx) => (
          <section
            key={section.id}
            id={section.id}
            ref={(el) => { sectionRefs.current[section.id] = el; }}
            className={`py-12 md:py-20 ${idx % 2 === 0 ? 'bg-background-50' : 'bg-background-100'}`}
          >
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              {/* Section Header */}
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-3 mb-8">
                <div>
                  <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold mb-3 ${section.accentClass}`}>
                    <i className={`${section.icon} text-xs`}></i>
                    {section.title}
                  </div>
                  <p className="text-sm text-foreground-500 max-w-lg leading-relaxed">
                    {section.subtitle}
                  </p>
                </div>
                <span className="text-[11px] font-semibold text-foreground-400 whitespace-nowrap">
                  {section.data.length} radio{section.data.length !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Grid - uniform, NO horizontal scroll */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                {section.data.map((radio) => (
                  <DiscoveryCard key={`${section.id}-${radio.id}`} radio={radio} sectionId={section.id} />
                ))}
              </div>
            </div>
          </section>
        ))}
      </main>

      <Footer />

      {/* Spacer para mobile */}
      <div className="md:hidden h-20"></div>
    </div>
  );
}